import { InjectionToken, Provider, Signal, computed, signal } from '@angular/core';
import { OSKUiLabels, OSKUiLabelsOverride, OSK_UI_LABELS_FR, mergeOSKUiLabels } from './osk-ui-labels';

/** Labels rendered by every @oskey/ui component. Defaults to French (`OSK_UI_LABELS_FR`). */
export const OSK_UI_LABELS = new InjectionToken<Signal<OSKUiLabels>>('OSK_UI_LABELS', {
  providedIn: 'root',
  factory: (): Signal<OSKUiLabels> => signal(OSK_UI_LABELS_FR).asReadonly()
});

/** BCP 47 locale used for month / weekday names and sorting. Defaults to 'fr-FR'. */
export const OSK_UI_LOCALE = new InjectionToken<Signal<string>>('OSK_UI_LOCALE', {
  providedIn: 'root',
  factory: (): Signal<string> => signal('fr-FR').asReadonly()
});

export interface OSKUiConfig {
  /** Label set the overrides apply to. Default: `OSK_UI_LABELS_FR`. */
  baseLabels?: OSKUiLabels;
  /**
   * Label overrides: a static object, or a factory run in an injection context that returns a
   * signal (e.g. built from your i18n service, so labels follow the active language).
   */
  labels?: OSKUiLabelsOverride | (() => Signal<OSKUiLabelsOverride>);
  /** Locale, or a factory run in an injection context that returns a signal of it. */
  locale?: string | (() => Signal<string>);
}

/**
 * Configures @oskey/ui labels and locale. Works in `bootstrapApplication` / `app.config.ts`
 * providers, on a lazy route, or on a single component.
 *
 * @example
 * provideOskUi({ baseLabels: OSK_UI_LABELS_EN, locale: 'en-GB' })
 * @example
 * // Follow the app's active language:
 * provideOskUi({
 *   labels: () => toSignal(inject(TranslateService).stream('oskUi'), { initialValue: {} }),
 *   locale: () => toSignal(inject(TranslateService).onLangChange.pipe(map((e) => e.lang)), { initialValue: 'fr-FR' })
 * })
 */
export function provideOskUi(config: OSKUiConfig): Provider[] {
  const providers: Provider[] = [];
  const { labels, baseLabels } = config;
  if (labels !== undefined || baseLabels !== undefined) {
    providers.push({
      provide: OSK_UI_LABELS,
      useFactory: (): Signal<OSKUiLabels> => {
        const base = baseLabels ?? OSK_UI_LABELS_FR;
        const overrides = typeof labels === 'function' ? labels() : signal(labels ?? {});
        return computed(() => mergeOSKUiLabels(base, overrides()));
      }
    });
  }
  const { locale } = config;
  if (locale !== undefined) {
    providers.push({
      provide: OSK_UI_LOCALE,
      useFactory: (): Signal<string> => (typeof locale === 'function' ? locale() : signal(locale).asReadonly())
    });
  }
  return providers;
}
